'use client'

// ============================================================
// Template "dashboard" — Painel de Plano de Negocios (interno)
// ============================================================
// Componente React client-side renderizado em /p/[slug] pelo runtime
// padrao de org_pages. Recebe { page, branding }:
//   - cores: derivadas de branding (primary/accent/secondary) via CSS
//     vars escopadas (.icdash) + color-mix. ZERO cor de marca hardcoded.
//   - dados: dataset git-data-driven em dashboard.assets.ts; page.data
//     pode sobrescrever os escalares (tagline / precos / defaults do sim).
// USO INTERNO: pagina nao publicada (is_published=false), acessivel so
// via preview token. A copy "para veicular" e gerada blind-copy
// (sem visto/imigracao/juridico/assessoria), conforme blindagem Stripe.
// ============================================================

import { useState, useMemo, type ReactNode } from 'react'
import {
  TrendingUp, Globe2, Users, Banknote, ArrowUpRight, Target,
  MapPin, Layers, Briefcase, PenTool, Cpu, Calculator, Coins,
  Plane, ShieldCheck, GraduationCap, Heart, Sparkles, Crown,
  Video, LineChart, Building2, Flag, Zap, CheckCircle2, Rocket,
} from 'lucide-react'
import type { OrgPage } from '@/lib/pages/types'
import type { OrgBranding } from '@/lib/supabase/org-branding'
import {
  DASH_COUNTRIES, DASH_LIFE_STAGES, DASH_STATUSES, DASH_PHASES, DASH_DEFAULTS,
  type DashCountry, type DashLifeStage, type DashStatus, type DashPhase, type DashDefaults,
} from '@/lib/pages/templates/dashboard.assets'

const fmt = (n: number) => n.toLocaleString('pt-BR')
const eur = (n: number) => '€' + n.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
const fmtPop = (n: number) =>
  n >= 1_000_000 ? (n / 1_000_000).toFixed(1).replace('.', ',') + ' mi' : Math.round(n / 1000) + ' mil'

/* eslint-disable @typescript-eslint/no-explicit-any */
const LIFE_ICONS: Record<string, any> = { Sparkles, Plane, ShieldCheck, Heart, GraduationCap, Crown }
const STATUS_ICONS: Record<string, any> = { Flag, Globe2 }

const SQUAD_META: Record<string, { icon: any; color: string; bg: string; ring: string }> = {
  Growth: { icon: TrendingUp, color: 'text-[var(--ic-bright)]', bg: 'bg-[var(--ic-a10)]', ring: 'ring-[var(--ic-a32)]' },
  Copy: { icon: PenTool, color: 'text-sky-300', bg: 'bg-sky-500/10', ring: 'ring-sky-500/30' },
  Commercial: { icon: Briefcase, color: 'text-amber-300', bg: 'bg-amber-500/10', ring: 'ring-amber-500/30' },
  Tech: { icon: Cpu, color: 'text-violet-300', bg: 'bg-violet-500/10', ring: 'ring-violet-500/30' },
  'Video Maker': { icon: Video, color: 'text-rose-300', bg: 'bg-rose-500/10', ring: 'ring-rose-500/30' },
  CFO: { icon: Banknote, color: 'text-[var(--ic-bright)]', bg: 'bg-[var(--ic-a10)]', ring: 'ring-[var(--ic-a32)]' },
  Strategy: { icon: Target, color: 'text-cyan-300', bg: 'bg-cyan-500/10', ring: 'ring-cyan-500/30' },
  'Full Squad': { icon: Users, color: 'text-slate-200', bg: 'bg-slate-500/10', ring: 'ring-slate-400/30' },
}

const SQUAD_LABELS: Record<string, string> = {
  Growth: 'Growth', Copy: 'Copy', Commercial: 'Comercial', Tech: 'Tech',
  'Video Maker': 'Video Maker', CFO: 'CFO', Strategy: 'Estrategia', 'Full Squad': 'Squad Completo',
}

/* ===========================================================
   Primitivos de UI
   =========================================================== */
function Bar({ value, max, className = 'bg-[var(--ic)]' }: { value: number; max: number; className?: string }) {
  const pct = Math.max(2, Math.min(100, (value / max) * 100))
  return (
    <div className="h-2 w-full rounded-full bg-slate-800 overflow-hidden">
      <div className={`h-full rounded-full ${className} transition-all duration-700 ease-out`}
           style={{ width: `${pct}%` }} />
    </div>
  )
}

function Stat({ icon: Icon, label, value, sub, accent = 'text-[var(--ic-bright)]' }:
  { icon: any; label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="group rounded-2xl border border-slate-800 bg-slate-900/60 p-5
                    transition-all duration-300 hover:border-[var(--ic-a42)] hover:bg-slate-900
                    hover:-translate-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</span>
        <Icon className={`h-4 w-4 ${accent} opacity-70 group-hover:opacity-100`} />
      </div>
      <div className={`mt-3 text-3xl font-semibold tracking-tight tabular-nums ${accent}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </div>
  )
}

/* ===========================================================
   ABA 1 — Visao do Investidor
   =========================================================== */
function InvestorView({ cfg, countries }: { cfg: DashDefaults; countries: DashCountry[] }) {
  const NET_INFLOW = cfg.netInflow
  const FE_PRICE = cfg.fePrice
  const MRR_PRICE = cfg.mrrPrice
  const [capture, setCapture] = useState(cfg.captureDefault)
  const [mrrConv, setMrrConv] = useState(cfg.mrrConvDefault)

  const sim = useMemo(() => {
    const rows: { y: number; feMembers: number; newMrr: number; cumulativeMrrSubs: number; feRevenue: number; arr: number }[] = []
    let cumulativeMrrSubs = 0
    for (let y = 1; y <= 5; y++) {
      const feMembers = Math.round(NET_INFLOW * (capture / 100))
      const newMrr = Math.round(feMembers * (mrrConv / 100))
      cumulativeMrrSubs += newMrr
      const feRevenue = feMembers * FE_PRICE
      const arr = cumulativeMrrSubs * MRR_PRICE * 12
      rows.push({ y, feMembers, newMrr, cumulativeMrrSubs, feRevenue, arr })
    }
    return rows
  }, [capture, mrrConv, NET_INFLOW, FE_PRICE, MRR_PRICE])

  const last = sim[sim.length - 1]
  const maxArr = Math.max(...sim.map((r) => r.arr))
  const maxStock = Math.max(...countries.map((c) => c.stock))

  return (
    <div className="space-y-8">
      {/* Tese principal: a arbitragem */}
      <div className="relative overflow-hidden rounded-3xl border border-[var(--ic-a24)]
                      bg-gradient-to-br from-slate-900 via-slate-900 to-[var(--ic-navy-soft)] p-7">
        <div className="relative z-10 grid gap-6 md:grid-cols-[1.2fr_1fr] md:items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--ic-a32)]
                             bg-[var(--ic-a10)] px-3 py-1 text-xs font-medium text-[var(--ic-bright)]">
              <Coins className="h-3.5 w-3.5" /> Motor de Arbitragem Cambial
            </span>
            <h2 className="mt-4 text-2xl font-semibold leading-tight text-white md:text-3xl">
              Os membros ganham em <span className="text-[var(--ic-bright)]">USD · GBP · EUR</span>.
              <br />Operamos em <span className="text-amber-300">BRL</span>.
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-300">
              Aceleracao da transicao de profissionais ibero-americanos para a Europa. A Brecha dos 2 Anos
              esta aberta agora. A margem esta na receita em moeda forte sobre uma base de custos em BRL.
            </p>
          </div>
          {/* Painel de arbitragem estilo cambio */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-950/60 p-5 font-mono">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>LADO DA RECEITA</span><span>LADO DO CUSTO</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div className="text-lg font-semibold text-[var(--ic-bright)]">USD / GBP / EUR</div>
              <ArrowUpRight className="h-5 w-5 text-[var(--ic-bright)]" />
              <div className="text-lg font-semibold text-amber-300">BRL</div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-center">
              <div className="rounded-xl bg-[var(--ic-a10)] py-3">
                <div className="text-2xl font-bold text-[var(--ic-bright)] tabular-nums">{eur(FE_PRICE)}</div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Front-end</div>
              </div>
              <div className="rounded-xl bg-[var(--ic-a10)] py-3">
                <div className="text-2xl font-bold text-[var(--ic-bright)] tabular-nums">{eur(MRR_PRICE)}<span className="text-sm">/mes</span></div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">MRR</div>
              </div>
            </div>
          </div>
        </div>
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[var(--ic-a12)] blur-3xl" />
      </div>

      {/* Cards de metricas-chave */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Globe2} label="TAM — Atual" value={cfg.tamNow} sub="emigrantes ibero-americanos no mundo" />
        <Stat icon={TrendingUp} label="TAM — 5 anos" value={cfg.tam5y} sub={cfg.tam5yDelta} accent="text-cyan-300" />
        <Stat icon={Users} label="Influxo Liquido Novo" value={cfg.netInflowLabel} sub="novos emigrantes saindo / ano" accent="text-amber-300" />
        <Stat icon={Banknote} label="Preco Recorrente" value={eur(MRR_PRICE)} sub="MRR por membro automatizado" />
      </div>

      {/* Simulador de 5 anos */}
      <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="flex flex-wrap items-center gap-2">
          <Calculator className="h-5 w-5 text-[var(--ic-bright)]" />
          <h3 className="text-lg font-semibold text-white">Simulador de Crescimento de 5 Anos</h3>
          <span className="ml-auto rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-300">
            Base: {fmt(NET_INFLOW)} de influxo / ano
          </span>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
          {/* Controles */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between text-sm">
                <label className="text-slate-300">Captura anual de mercado</label>
                <span className="font-mono font-semibold text-[var(--ic-bright)]">{capture.toFixed(2).replace('.', ',')}%</span>
              </div>
              <input type="range" min="0.05" max="3" step="0.05" value={capture}
                     onChange={(e) => setCapture(parseFloat(e.target.value))}
                     className="mt-2 w-full accent-[var(--ic)]" />
              <p className="mt-1 text-xs text-slate-500">
                ≈ {fmt(Math.round(NET_INFLOW * (capture / 100)))} membros front-end / ano
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm">
                <label className="text-slate-300">Conversao front-end para MRR</label>
                <span className="font-mono font-semibold text-[var(--ic-bright)]">{mrrConv}%</span>
              </div>
              <input type="range" min="2" max="60" step="1" value={mrrConv}
                     onChange={(e) => setMrrConv(parseInt(e.target.value))}
                     className="mt-2 w-full accent-[var(--ic)]" />
              <p className="mt-1 text-xs text-slate-500">tornam-se assinantes recorrentes de {eur(MRR_PRICE)}/mes</p>
            </div>

            <div className="rounded-2xl border border-[var(--ic-a24)] bg-[var(--ic-a06)] p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400">ARR projetado no Ano 5</div>
              <div className="mt-1 text-3xl font-bold text-[var(--ic-bright)] tabular-nums">
                {eur(last.arr)}
              </div>
              <div className="mt-1 text-xs text-slate-400">
                {fmt(last.cumulativeMrrSubs)} assinantes MRR ativos
              </div>
            </div>
          </div>

          {/* Grafico de barras nativo */}
          <div>
            <div className="mb-3 text-xs uppercase tracking-wide text-slate-400">ARR projetado por ano</div>
            <div className="space-y-3">
              {sim.map((r) => (
                <div key={r.y} className="flex items-center gap-3">
                  <span className="w-12 shrink-0 text-xs font-medium text-slate-400">Ano {r.y}</span>
                  <div className="flex-1">
                    <Bar value={r.arr} max={maxArr}
                         className="bg-gradient-to-r from-[var(--ic-deep)] to-[var(--ic)]" />
                  </div>
                  <span className="w-28 shrink-0 text-right font-mono text-sm font-semibold text-[var(--ic-bright)] tabular-nums">
                    {eur(r.arr)}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              Modelo cumulativo: cada ano adiciona novos assinantes MRR sobre a base anterior. A receita de
              membership do front-end e unica; a receita recorrente se acumula. Os custos operacionais alavancados
              em BRL sao excluidos aqui para isolar a escalabilidade do faturamento. As comissoes de back-end
              (seguros e finder fees) nao entram neste recorte e ampliam a margem real.
            </p>
          </div>
        </div>
      </div>

      {/* Alvos geograficos */}
      <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[var(--ic-bright)]" />
          <h3 className="text-lg font-semibold text-white">Alvos Geograficos Principais</h3>
          <span className="ml-auto text-xs text-slate-400">Estoque vs. influxo anual</span>
        </div>
        <div className="mt-5 space-y-4">
          {countries.map((c) => (
            <div key={c.id} className="group rounded-2xl border border-slate-800 bg-slate-950/40 p-4
                                       transition-colors hover:border-[var(--ic-a32)]">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-xl">{c.flag}</span>
                <span className="font-medium text-white">{c.name}</span>
                <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs font-mono text-slate-300">
                  {c.stock >= 1_000_000 ? (c.stock / 1_000_000).toFixed(1).replace('.', ',') + 'M' : fmt(c.stock / 1000) + 'k'} estoque
                </span>
                <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-mono text-amber-300">
                  +{fmt(c.newYear / 1000)}k / ano
                </span>
                <span className="ml-auto hidden text-xs text-slate-400 md:block">{c.focus}</span>
              </div>
              <div className="mt-3">
                <Bar value={c.stock} max={maxStock}
                     className="bg-gradient-to-r from-[var(--ic-deep)] to-[var(--ic)]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ===========================================================
   ABA 2 — Matriz de Marketing (Roteador de Personas)
   =========================================================== */
function MarketingMatrix({ countries, statuses, stages }:
  { countries: DashCountry[]; statuses: DashStatus[]; stages: DashLifeStage[] }) {
  const [countryId, setCountryId] = useState('uk')
  const [statusId, setStatusId] = useState('emigrant')
  const [stageId, setStageId] = useState('family')

  const country = countries.find((c) => c.id === countryId) || countries[0]
  const status = statuses.find((s) => s.id === statusId) || statuses[0]
  const stage = stages.find((s) => s.id === stageId) || stages[0]

  const composed = useMemo(() => {
    const isEmigrant = statusId === 'emigrant'
    // BLIND-COPY: envelope proprietario, sem visto/imigracao/juridico/assessoria.
    const hook = isEmigrant
      ? `Cansado de ${country.pain}? Existe um protocolo mais inteligente para se estabelecer na Europa.`
      : `Antes de sair de ${country.name === 'Espanha' ? 'um status provisorio' : 'sua terra natal'}, estruture a jornada com dados, nao com achismo.`

    const angle = isEmigrant
      ? `${stage.angle} · Escape da crise local`
      : `${stage.angle} · Internacionalizacao estruturada`

    const moeda = country.name === 'Estados Unidos' ? 'em dolar' : country.name === 'Reino Unido' ? 'em libra' : 'atual'

    const copy = isEmigrant
      ? `Voce cruzou o oceano por uma vida melhor e hoje enfrenta ${country.pain}. O Protocolo de Aceleracao Ibero-Americano organiza sua transicao para a Europa com inteligencia de dados e conformidade, preservando sua renda ${moeda} enquanto voce constroi ${stage.drive}. A Brecha dos 2 Anos esta aberta. Nossa equipe cuida da infraestrutura.`
      : `Sair de ${country.name} sem metodo custa dinheiro e tempo. A IberoConnect mapeia seu Protocolo de Aceleracao com dados e conformidade, preservando sua renda em moeda forte enquanto voce garante ${stage.drive}. A Brecha dos 2 Anos, planejada desde o primeiro dia.`

    return { hook, angle, copy }
  }, [country, statusId, stage])

  const Selector = ({ active, onClick, children }:
    { active: boolean; onClick: () => void; children: ReactNode }) => (
    <button onClick={onClick}
      className={`rounded-xl border px-3.5 py-2 text-sm font-medium transition-all
        ${active
          ? 'border-[var(--ic-a60)] bg-[var(--ic-a16)] text-[var(--ic-bright)] shadow-[0_0_0_1px_var(--ic-a30)]'
          : 'border-slate-800 bg-slate-900/60 text-slate-300 hover:border-slate-600 hover:text-white'}`}>
      {children}
    </button>
  )

  return (
    <div className="space-y-6">
      {/* Tabela: volume de mercado + gatilho de copy por pais */}
      <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="flex items-center gap-2">
          <Globe2 className="h-5 w-5 text-[var(--ic-bright)]" />
          <h3 className="text-lg font-semibold text-white">Volume de Mercado &times; Gatilho de Copy</h3>
          <span className="ml-auto text-xs text-slate-400">Por pais de referencia</span>
        </div>
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[680px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-400">
                <th className="px-3 py-2.5 font-medium">Pais</th>
                <th className="px-3 py-2.5 font-medium">
                  Publico 1
                  <span className="block text-[10px] font-normal normal-case tracking-normal text-slate-500">Na terra natal · novos/ano</span>
                </th>
                <th className="px-3 py-2.5 font-medium">
                  Publico 2
                  <span className="block text-[10px] font-normal normal-case tracking-normal text-slate-500">Ja emigrantes · estoque</span>
                </th>
                <th className="px-3 py-2.5 font-medium">Gatilho de copy para a Espanha</th>
              </tr>
            </thead>
            <tbody>
              {countries.map((c) => (
                <tr key={c.id} className="border-b border-slate-800/70 align-top transition-colors hover:bg-slate-950/40">
                  <td className="whitespace-nowrap px-3 py-3 font-medium text-white">
                    <span className="mr-1.5">{c.flag}</span>{c.name}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 font-mono tabular-nums text-amber-300">{fmt(c.newYear)} / ano</td>
                  <td className="whitespace-nowrap px-3 py-3 font-mono tabular-nums text-[var(--ic-bright)]">{fmtPop(c.stock)}</td>
                  <td className="px-3 py-3 leading-relaxed text-slate-300">{c.gatilho}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
          <p className="text-xs leading-relaxed text-amber-200/80">
            Os gatilhos nomeiam mecanismos (visto, Lei Beckham, passaporte) como ANGULO INTERNO de targeting. Antes de
            veicular, envelopar em blind-copy (Protocolo de Aceleracao / A Brecha dos 2 Anos), sem visto/imigracao/juridico/assessoria.
          </p>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-[var(--ic-bright)]" />
          <h3 className="text-lg font-semibold text-white">Roteador de Personas</h3>
          <span className="ml-auto text-xs text-slate-400">Pais × Status × Fase da Vida</span>
        </div>

        {/* Eixo 1: Pais */}
        <div className="mt-6">
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">Pais</div>
          <div className="flex flex-wrap gap-2">
            {countries.map((c) => (
              <Selector key={c.id} active={c.id === countryId} onClick={() => setCountryId(c.id)}>
                <span className="mr-1">{c.flag}</span>{c.name}
              </Selector>
            ))}
          </div>
        </div>

        {/* Eixo 2: Status */}
        <div className="mt-5">
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">Status</div>
          <div className="flex flex-wrap gap-2">
            {statuses.map((s) => {
              const Icon = STATUS_ICONS[s.iconKey] || Flag
              return (
                <Selector key={s.id} active={s.id === statusId} onClick={() => setStatusId(s.id)}>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5" />{s.label}
                    <span className="text-xs text-slate-500">({s.short})</span>
                  </span>
                </Selector>
              )
            })}
          </div>
        </div>

        {/* Eixo 3: Fase da vida */}
        <div className="mt-5">
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-400">Fase da Vida</div>
          <div className="flex flex-wrap gap-2">
            {stages.map((s) => {
              const Icon = LIFE_ICONS[s.iconKey] || Sparkles
              return (
                <Selector key={s.id} active={s.id === stageId} onClick={() => setStageId(s.id)}>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5" />{s.label}
                  </span>
                </Selector>
              )
            })}
          </div>
        </div>
      </div>

      {/* Painel de saida */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-[var(--ic-a32)] bg-[var(--ic-a06)] p-5">
          <div className="flex items-center gap-2 text-[var(--ic-bright)]">
            <Zap className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Gancho</span>
          </div>
          <p className="mt-3 text-base font-medium leading-snug text-white">{composed.hook}</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <div className="flex items-center gap-2 text-cyan-300">
            <Target className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Abordagem</span>
          </div>
          <p className="mt-3 text-base font-medium leading-snug text-slate-100">{composed.angle}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-300">{country.flag} {country.name}</span>
            <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-300">{status.short}</span>
            <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-300">{stage.label}</span>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <div className="flex items-center gap-2 text-amber-300">
            <PenTool className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Estrategia Central</span>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">{status.summary}</p>
          <p className="mt-2 text-xs text-slate-500">Gancho ancora: &ldquo;{status.coreHook}&rdquo;</p>
        </div>
      </div>

      {/* Estrategia de copy */}
      <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-6">
        <div className="flex items-center gap-2">
          <PenTool className="h-5 w-5 text-[var(--ic-bright)]" />
          <h3 className="text-lg font-semibold text-white">Copy de Anuncio Pronto para Veicular (blind-copy)</h3>
        </div>
        <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/60 p-5">
          <p className="text-[15px] leading-relaxed text-slate-200">{composed.copy}</p>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
          <p className="text-xs leading-relaxed text-amber-200/80">
            Blindagem Stripe: esta copy NAO usa &ldquo;visto / imigracao / juridico / assessoria&rdquo;. O mecanismo fica
            envelopado (Protocolo de Aceleracao, A Brecha dos 2 Anos). Termos tecnicos so na area de membros paga.
          </p>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Gerado ao vivo a partir do perfil de dor de {country.name}, da intencao {status.short} e dos drivers de {stage.label}.
        </p>
      </div>
    </div>
  )
}

/* ===========================================================
   ABA 3 — Playbook de Operacoes
   =========================================================== */
function OperationsPlaybook({ phases }: { phases: DashPhase[] }) {
  const [activeId, setActiveId] = useState(phases[0]?.id || 'p1')
  const active = phases.find((p) => p.id === activeId) || phases[0]

  return (
    <div className="space-y-6">
      {/* Linha do tempo */}
      <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-[var(--ic-bright)]" />
          <h3 className="text-lg font-semibold text-white">Linha do Tempo de Execucao</h3>
          <span className="ml-auto text-xs text-slate-400">Clique em uma fase</span>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-3">
          {phases.map((p) => {
            const isActive = p.id === activeId
            return (
              <button key={p.id} onClick={() => setActiveId(p.id)}
                className={`group relative overflow-hidden rounded-2xl border p-5 text-left transition-all
                  ${isActive
                    ? 'border-[var(--ic-a60)] bg-[var(--ic-a10)]'
                    : 'border-slate-800 bg-slate-950/40 hover:border-slate-600'}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-3xl font-bold ${isActive ? 'text-[var(--ic-bright)]' : 'text-slate-600'}`}>
                    {p.n}
                  </span>
                  <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs font-mono text-slate-300">{p.window}</span>
                </div>
                <div className="mt-3 font-semibold text-white">{p.name}</div>
                <div className="mt-3">
                  <Bar value={p.progress} max={100}
                       className={isActive ? 'bg-[var(--ic)]' : 'bg-slate-600'} />
                  <div className="mt-1 text-right text-xs text-slate-400 tabular-nums">{p.progress}%</div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Detalhe da fase ativa */}
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
          <div className="flex items-center gap-3">
            <span className="font-mono text-2xl font-bold text-[var(--ic-bright)]">{active.n}</span>
            <div>
              <h3 className="text-lg font-semibold text-white">{active.name}</h3>
              <span className="text-xs text-slate-400">{active.window}</span>
            </div>
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[var(--ic-a10)]
                             px-3 py-1 text-xs font-medium text-[var(--ic-bright)]">
              <Rocket className="h-3.5 w-3.5" />{active.progress}% concluido
            </span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">{active.focus}</p>

          <div className="mt-5 space-y-2.5">
            {active.tasks.map((t, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-800
                                      bg-slate-950/40 p-3.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ic-bright)]" />
                <span className="text-sm text-slate-200">{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mapeamento do squad */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[var(--ic-bright)]" />
            <h3 className="text-lg font-semibold text-white">Squad em Acao</h3>
          </div>
          <div className="mt-5 space-y-3">
            {active.squad.map((role) => {
              const meta = SQUAD_META[role] || SQUAD_META['Full Squad']
              const Icon = meta.icon
              return (
                <div key={role}
                  className={`flex items-center gap-3 rounded-2xl border border-slate-800 ${meta.bg}
                              p-4 ring-1 ${meta.ring}`}>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-950/50">
                    <Icon className={`h-5 w-5 ${meta.color}`} />
                  </div>
                  <div>
                    <div className={`font-medium ${meta.color}`}>{SQUAD_LABELS[role] || role}</div>
                    <div className="text-xs text-slate-400">Responsavel por esta fase</div>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="mt-5 rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-400">Mercado-alvo da fase</div>
            <p className="mt-1.5 text-sm text-slate-200">{active.market}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ===========================================================
   Raiz — template renderizado pelo runtime de org_pages
   =========================================================== */
export function DashboardTemplate({ page, branding }: { page: OrgPage; branding: OrgBranding }) {
  const d = (page.data || {}) as Partial<DashDefaults> & {
    countries?: DashCountry[]; statuses?: DashStatus[]; stages?: DashLifeStage[]; phases?: DashPhase[]
  }
  const cfg: DashDefaults = { ...DASH_DEFAULTS, ...d }
  const countries = d.countries && d.countries.length ? d.countries : DASH_COUNTRIES
  const statuses = d.statuses && d.statuses.length ? d.statuses : DASH_STATUSES
  const stages = d.stages && d.stages.length ? d.stages : DASH_LIFE_STAGES
  const phases = d.phases && d.phases.length ? d.phases : DASH_PHASES

  const primary = branding.primaryColor || '#2563EB'
  const accent = branding.accentColor || '#1E40AF'
  const navy = branding.secondaryColor || '#0A1D45'
  const brandName = branding.siteTitle || page.title || 'IberoConnect'
  const logoUrl = branding.logoUrl

  // Paleta escopada derivada do branding (color-mix em CSS = robusto no Tailwind v4).
  const css = `
.icdash{
  --ic:${primary};
  --ic-deep:${accent};
  --ic-navy:${navy};
  --ic-bright:color-mix(in srgb, ${primary} 58%, white);
  --ic-a06:color-mix(in srgb, ${primary} 6%, transparent);
  --ic-a10:color-mix(in srgb, ${primary} 10%, transparent);
  --ic-a12:color-mix(in srgb, ${primary} 13%, transparent);
  --ic-a16:color-mix(in srgb, ${primary} 16%, transparent);
  --ic-a24:color-mix(in srgb, ${primary} 24%, transparent);
  --ic-a30:color-mix(in srgb, ${primary} 30%, transparent);
  --ic-a32:color-mix(in srgb, ${primary} 32%, transparent);
  --ic-a42:color-mix(in srgb, ${primary} 42%, transparent);
  --ic-a60:color-mix(in srgb, ${primary} 60%, transparent);
  --ic-navy-soft:color-mix(in srgb, ${navy} 60%, #020617);
}`

  const [tab, setTab] = useState('investor')
  const TABS = [
    { id: 'investor', label: 'Visao do Investidor', icon: LineChart },
    { id: 'marketing', label: 'Matriz de Marketing', icon: Target },
    { id: 'operations', label: 'Playbook de Operacoes', icon: Layers },
  ]

  return (
    <div className="icdash min-h-screen bg-slate-950 text-slate-100 antialiased">
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {/* Cabecalho */}
        <header className="flex flex-wrap items-center gap-4">
          {logoUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={logoUrl} alt={brandName} className="h-12 w-auto max-w-[180px] object-contain" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br
                            from-[var(--ic)] to-[var(--ic-deep)] shadow-[0_8px_30px_-12px_var(--ic)]">
              <Building2 className="h-6 w-6 text-slate-950" />
            </div>
          )}
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-white">{brandName}</h1>
            <p className="text-xs text-slate-400">{cfg.brandTagline}</p>
          </div>
          <div className="ml-auto hidden items-center gap-2 rounded-full border border-[var(--ic-a24)]
                          bg-[var(--ic-a06)] px-3 py-1.5 text-xs text-[var(--ic-bright)] sm:flex">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--ic)]" />
            Modelo ao vivo
          </div>
        </header>

        {/* Abas */}
        <nav className="mt-7 flex gap-1.5 rounded-2xl border border-slate-800 bg-slate-900/60 p-1.5">
          {TABS.map((t) => {
            const Icon = t.icon
            const active = tab === t.id
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5
                  text-sm font-medium transition-all
                  ${active
                    ? 'bg-[var(--ic-a16)] text-[var(--ic-bright)] shadow-[inset_0_0_0_1px_var(--ic-a30)]'
                    : 'text-slate-400 hover:bg-slate-800/60 hover:text-white'}`}>
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            )
          })}
        </nav>

        {/* Conteudo */}
        <main className="mt-7">
          {tab === 'investor' && <InvestorView cfg={cfg} countries={countries} />}
          {tab === 'marketing' && <MarketingMatrix countries={countries} statuses={statuses} stages={stages} />}
          {tab === 'operations' && <OperationsPlaybook phases={phases} />}
        </main>

        <footer className="mt-10 border-t border-slate-800 pt-5 text-center text-xs text-slate-500">
          {brandName} · Painel Interativo de Plano de Negocios · Uso interno · Os numeros sao projecoes de planejamento, nao garantias.
        </footer>
      </div>
    </div>
  )
}

export default DashboardTemplate
